/**
 * <accordion-group> — progressive enhancement around a set of <details> rows.
 *
 * The rows work fully without this script: native <details>/<summary> already handles
 * toggling, keyboard operation and screen-reader semantics. This element adds only what
 * native markup cannot express:
 *
 *   - single-open mode (`single` attribute), closing siblings when one opens
 *   - an animated height transition, since <details> snaps open by default
 *
 * Both respect `prefers-reduced-motion`: when motion is reduced the transition is
 * skipped and the row simply opens.
 */

// Wrapped so nothing here reaches global scope: these are classic scripts, and a
// top-level name shared with another file makes the whole file throw before it can
// register its element. Also what makes a duplicate <script> tag for this file safe.
(() => {
  class AccordionGroup extends HTMLElement {
    connectedCallback() {
      this.single = this.hasAttribute('single');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.rows = [...this.querySelectorAll('details.accordion-item')];
      this.rows.forEach((row) => this.enhance(row));
    }

    enhance(row) {
      const summary = row.querySelector('summary');
      const panel = row.querySelector('.accordion-item__panel');
      if (!summary || !panel) return;

      summary.addEventListener('click', (event) => {
        // Take over the toggle so the panel can be animated in both directions.
        event.preventDefault();

        if (row.open) {
          this.collapse(row, panel);
        } else {
          if (this.single) this.closeSiblings(row);
          this.expand(row, panel);
        }
      });
    }

    closeSiblings(current) {
      this.rows
        .filter((row) => row !== current && row.open)
        .forEach((row) => this.collapse(row, row.querySelector('.accordion-item__panel')));
    }

    expand(row, panel) {
      row.open = true;

      if (this.reduceMotion.matches) return;

      const target = panel.scrollHeight;
      panel.style.height = '0px';

      requestAnimationFrame(() => {
        panel.style.height = `${target}px`;
      });

      panel.addEventListener(
        'transitionend',
        () => {
          // Release the fixed height so the panel can reflow with its content.
          panel.style.height = '';
        },
        { once: true }
      );
    }

    collapse(row, panel) {
      if (this.reduceMotion.matches) {
        row.open = false;
        return;
      }

      panel.style.height = `${panel.scrollHeight}px`;

      requestAnimationFrame(() => {
        panel.style.height = '0px';
      });

      panel.addEventListener(
        'transitionend',
        () => {
          row.open = false;
          panel.style.height = '';
        },
        { once: true }
      );
    }
  }

  if (!customElements.get('accordion-group')) {
    customElements.define('accordion-group', AccordionGroup);
  }
})();
